import React, { useState, useEffect, useRef } from 'react';
import { Button, Card, Row, Col, Form, Alert, Modal, Spinner } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { createHTMLInstance } from '../../../Models/HTML';

const defaultHTML = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Custom HTML Block</title>
    <style>
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 0; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    </style>
</head>
<body>
    <div class="container">
        <h2>Custom HTML Block</h2>
        <p>Edit this HTML as you like!</p>
    </div>
</body>
</html>`;

const HTMLBuilder = ({ id }) => {
  const [htmlInstance] = useState(createHTMLInstance());
  const [loading, setLoading] = useState(false);
  const [htmlContent, setHtmlContent] = useState(defaultHTML);
  const [blockName, setBlockName] = useState('My HTML Block');
  const [description, setDescription] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    getData();
    // eslint-disable-next-line
  }, []);

  const getData = async () => {
    try {
      setLoading(true);
      if (id) {
        await htmlInstance.load(id);
      } else {
        await htmlInstance.load();
      }
      setBlockName(htmlInstance.getName() || 'My HTML Block');
      setDescription(htmlInstance.getDescription() || '');
      setHtmlContent(htmlInstance.getHtmlContent() || defaultHTML);
    } catch (error) {
      toast.error('Failed to load HTML block data');
      setBlockName('My HTML Block');
      setDescription('');
      setHtmlContent(defaultHTML);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (iframeRef.current && showPreview) {
      const iframe = iframeRef.current;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(htmlContent);
      iframeDoc.close();
    }
  }, [htmlContent, showPreview]);

  const handleSave = async () => {
    setLoading(true);
    try {
      htmlInstance.setName(blockName);
      htmlInstance.setDescription(description);
      htmlInstance.setHtmlContent(htmlContent);
      await htmlInstance.save();
      toast.success('HTML block saved successfully!');
      await getData();
    } catch (error) {
      toast.error('Failed to save HTML block. Please try again.');
      console.error('Error saving HTML block:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setHtmlContent(defaultHTML);
    setBlockName('My HTML Block');
    setDescription('');
    setShowResetModal(false);
    toast.info('HTML block reset to default');
  };

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>HTML Block Builder</h3>
        <div className="d-flex gap-2">
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </Button>
          <Button 
            variant="outline-warning" 
            onClick={() => setShowResetModal(true)}
          >
            Reset to Default
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? <Spinner animation="border" size="sm" /> : 'Save HTML Block'}
          </Button>
        </div>
      </div>

      <Row>
        <Col lg={showPreview ? 6 : 12}>
          <Card className="mb-3">
            <Card.Header>
              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Block Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={blockName}
                      onChange={(e) => setBlockName(e.target.value)}
                      placeholder="Enter block name"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      type="text"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Enter description (optional)"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body>
              <Form.Group>
                <Form.Label>HTML Content</Form.Label>
                <Form.Control
                  as="textarea"
                  id="html-editor"
                  rows={25}
                  value={htmlContent}
                  onChange={(e) => setHtmlContent(e.target.value)}
                  style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                />
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>

        {showPreview && (
          <Col lg={6}>
            <Card>
              <Card.Header>Preview</Card.Header>
              <Card.Body style={{ padding: 0 }}>
                <iframe
                  ref={iframeRef}
                  title="HTML Block Preview"
                  style={{
                    width: '100%',
                    height: '600px',
                    border: 'none',
                    backgroundColor: 'white'
                  }}
                />
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>

      <Alert variant="info" className="mt-3">
        <strong>Tips:</strong>
        <ul className="mb-0 mt-2">
          <li>Edit your HTML freely in the editor</li>
          <li>Click "Show Preview" to see how your block will look</li>
          <li>Use the block name and description to organize your HTML blocks</li>
          <li>The preview is isolated in an iframe to prevent styling conflicts</li>
        </ul>
      </Alert>

      {/* Reset Confirmation Modal */}
      <Modal show={showResetModal} onHide={() => setShowResetModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Reset</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to reset to the default HTML block? This will lose all your changes.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResetModal(false)}>
            Cancel
          </Button>
          <Button variant="warning" onClick={handleReset}>
            Reset HTML Block
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="top-right" />
    </div>
  );
};

export default HTMLBuilder;
